import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useMerchants } from '../../merchants/hooks/useMerchants';
import { useGenerateReport, useIntelligenceReports } from '../hooks/useIntelligenceReports';
import { GenerateReportModal } from '../components/GenerateReportModal';
import { formatPeriod } from '../period';
import {
  GENERATION_METHOD_LABELS,
  STATUS_HINTS,
  STATUS_LABELS,
  STATUS_STYLES,
  type IntelligenceReportStatus,
} from '../types';

const STATUS_FILTERS: Array<{ value: IntelligenceReportStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'PENDING_REVIEW', label: 'Pending review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'RELEASED', label: 'Released' },
];

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/**
 * Monthly Intelligence Report — TCS admin review queue (PE-2141, backend PE-2140).
 *
 * TCS-staff only. osprey-api's controller sits behind CollectiveOnlyGuard, so a
 * merchant reaching this page would 403 on every request; the route and sidebar
 * entry are gated on INTELLIGENCE_REPORT_VIEW, which is granted to COLLECTIVE
 * roles only for exactly that reason (same pattern as CAMPAIGN_ADMIN_VIEW).
 */
export function IntelligenceReportsPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canGenerate = hasPermission(Permission.INTELLIGENCE_REPORT_GENERATE);

  const [status, setStatus] = useState<IntelligenceReportStatus | ''>('');
  const [merchantId, setMerchantId] = useState('');
  const [period, setPeriod] = useState('');

  const query = useMemo(
    () => ({
      ...(status ? { status } : {}),
      ...(merchantId ? { merchantId } : {}),
      ...(period ? { period } : {}),
    }),
    [status, merchantId, period],
  );

  const { data: reports = [], isLoading, error, isFetching } = useIntelligenceReports(query);
  const { data: merchants = [] } = useMerchants();
  const generate = useGenerateReport();

  const [generateOpen, setGenerateOpen] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);

  const merchantName = useMemo(() => {
    const map = new Map<string, string>();
    (merchants as Array<{ id: string; name: string }>).forEach((m) => map.set(m.id, m.name));
    return map;
  }, [merchants]);

  const hasFilters = !!status || !!merchantId || !!period;

  const handleGenerate = async (values: { merchantId: string; period: string; force: boolean }) => {
    try {
      const created = await generate.mutateAsync(values);
      setGenerateOpen(false);
      setConflict(null);
      navigate(`/intelligence-reports/${created.id}`);
    } catch (err) {
      // 409 = a report already exists for that merchant/period. Keep the modal
      // open and flip it to the explicit destructive-regenerate confirmation
      // rather than making the user guess what went wrong.
      if ((err as { status?: number })?.status === 409) {
        setConflict((err as { message?: string })?.message ?? 'A report already exists for this period.');
        return;
      }
      setGenerateOpen(false);
    }
  };

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Intelligence Reports</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            AI-drafted monthly customer-behaviour reports. Every draft is reviewed by TCS before the
            merchant can see it.
          </p>
        </div>
        {canGenerate && (
          <Button
            size="sm"
            className="h-[40px] shrink-0"
            onClick={() => {
              setConflict(null);
              setGenerateOpen(true);
            }}
          >
            Generate report
          </Button>
        )}
      </div>

      {/* ---- Filters ---- */}
      <div className="bg-card rounded-2xl border border-border p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <span className="block text-[13px] font-medium text-foreground mb-1">Status</span>
            {/*
              Segmented buttons rather than a dropdown: four short options, and
              the shared <Select> needs a DropdownProvider that Osprey has no
              global instance of yet (PE-2216).
            */}
            <div className="inline-flex rounded-[8px] border border-border overflow-hidden">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value || 'all'}
                  type="button"
                  onClick={() => setStatus(f.value)}
                  className={`h-[38px] px-3 text-sm transition-colors ${
                    status === f.value
                      ? 'bg-muted font-medium text-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Merchant</span>
            <select
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              className="mt-1 h-[40px] min-w-[220px] rounded-[8px] border border-border bg-background px-3 text-sm text-foreground"
            >
              <option value="">All merchants</option>
              {(merchants as Array<{ id: string; name: string }>).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Period</span>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm text-foreground"
            />
          </label>

          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              className="h-[40px]"
              onClick={() => {
                setStatus('');
                setMerchantId('');
                setPeriod('');
              }}
            >
              Clear
            </Button>
          )}
          {isFetching && <span className="text-[12px] text-muted-foreground pb-2">Refreshing…</span>}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading intelligence reports…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">
          Failed to load intelligence reports. Please try again.
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-border rounded-[16px] px-6">
          {hasFilters ? (
            <>No reports match these filters.</>
          ) : (
            <>
              <p>No intelligence reports have been generated yet.</p>
              <p className="mt-2 text-[13px]">
                Reports are drafted automatically each month for merchants that have an analytics
                configuration.
                {canGenerate && ' You can also generate one for a specific merchant and period.'}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Period</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Source</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Generated</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Released</th>
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reports.map((report) => {
                  const draft = report.versions.find((v) => v.versionType === 'AI_DRAFT');
                  return (
                    <tr key={report.id} className="hover:bg-muted transition-colors align-middle">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {merchantName.get(report.merchantId) ?? (
                          <span className="text-muted-foreground font-mono text-xs">
                            {report.merchantId}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                        {formatPeriod(report.period)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${STATUS_STYLES[report.status]}`}
                          title={STATUS_HINTS[report.status]}
                        >
                          {STATUS_LABELS[report.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {draft ? GENERATION_METHOD_LABELS[draft.generationMethod] : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                        {fmtDate(report.generatedAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                        {fmtDate(report.releasedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/intelligence-reports/${report.id}`)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {report.status === 'PENDING_REVIEW' ? 'Review' : 'View'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <GenerateReportModal
        isOpen={generateOpen}
        onClose={() => {
          setGenerateOpen(false);
          setConflict(null);
        }}
        onSubmit={handleGenerate}
        isSubmitting={generate.isPending}
        conflict={conflict}
      />
    </div>
  );
}
